import React, { 
  forwardRef,
  Ref,
  useImperativeHandle,
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback
} from "react";
import {
  TextField,
  Box,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { StyledTooltip } from "../../components/styled-tooltip/styled-tooltip";
import pubSub, { EVENTS } from "../../config/pubSub";
import { ApplicationsContext } from "./applications-context";
import { useService } from "./applications-service";
import { AppConfig } from "../../context/context";
import { StyledButton } from "../../components/styled-button/styled-button";

type INameSubscriptionStepModel = {
  id: string;
  name: string;
  clientId: string;
  accessKey: string;
};

export type NotifyUsersList = {
  notifyUsers?: string[];
};

export type IAddEditProviderModel = {
  0: INameSubscriptionStepModel;
  isValid: boolean;
  isSameValue?: boolean | undefined;
};

export type IEditProviderModel = {
  0: INameSubscriptionStepModel;
  isValid: boolean;
};

const FormWrapper = styled(Box)(({ theme }) => ({
  "& label.MuiFormLabel-root": {
    fontSize: "inherit",
  },
  "& strong": {
    margin: theme.spacing(1),
    marginTop: theme.spacing(2),
    marginLeft: 0,
    display: "inline-block",
  },
  "& .MuiTextField-root": {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    "& div.MuiOutlinedInput-root": {
      fontSize: "inherit",
      padding: theme.spacing(1),
      borderRadius: "8px",
    },
  },
  "& div.url-field": {
    margin: theme.spacing(1),
    marginLeft: 0,
    "& strong": {
      marginTop: 0,
      marginRight: theme.spacing(1),
    },
  },
  "& .label": {
    marginBottom: 0,
  },
}));

interface IValidatorProps {
  model: INameSubscriptionStepModel;
  validate: any;
}

const validateName = (props: IValidatorProps) => {
  const { validate, model } = props;

  if (!validate.name) {
    validate.name = {
      touched: true,
      error: false,
    };
  }

  validate.name.message = "";

  if (!model.name) {
    validate.name.message = "validate_required";
    validate.name.error = true;
    return validate;
  }

  if (model.name.length < 3) {
    validate.name.message = "validate_minlength";
    validate.name.minLength = 3;
    validate.name.error = true;
    return validate;
  } else {
    validate.name.message = "validate_minlength";
    validate.name.minLength = 3;
    validate.name.error = false;
    return validate;
  }
};

export const validateStep = (
  model: INameSubscriptionStepModel,
  validate: any,
) => {
  return {
    ...validateName({ model, validate })
  };
  
};

export interface IOrgUpsertRefObject {
  resetValidate: () => void;
}

type IProps = {
  model: IAddEditProviderModel;
  handleModelChange: (updatedModel: any) => void;
  containerClass?: string;
  readOnlyName?: boolean;
  readOnlyEmail?: boolean;
  initialEmail?: string;
};

const ApplicationUpsertForm = forwardRef((props: IProps, ref: Ref<IOrgUpsertRefObject>) => {
  const { t } = useTranslation();
  const [validate, setValidate] = useState<any>({});
  const { selectedApp, onEditValidation, originalAppName } = useContext(ApplicationsContext);
  const [openTooltips, setOpenTooltips] = useState<any>({});
  const [previousAppName, setPreviousAppName] = useState<string | null>(null);
  const { appService } = useService();
  const { config } = useContext(AppConfig);
  const accessKeyRef = useRef<HTMLInputElement>(null); 
  const clientIdRef = useRef<HTMLInputElement>(null);
  const [copyButtonText, setCopyButtonText] = useState<string>(
    t("click_to_copy") || ""
  );

  useImperativeHandle(
    ref,
    () => ({
      resetValidate: () => {
        setValidate({});
      },
    }),
    []
  );

  useEffect(() => {
    if (selectedApp && originalAppName) {
      setPreviousAppName(
        originalAppName // spread all properties
      );
    } else {
      setPreviousAppName(null); // Clear previous values when no app is selected
    }
  }, [selectedApp, originalAppName]);

  useEffect(() => {
    if (!selectedApp) {
      const updatedModel = JSON.parse(JSON.stringify(props.model[0]));    
      updateParentModel(updatedModel, "name");
    }
  }, [selectedApp]);

  useEffect(() => {
    pubSub.subscribe(EVENTS.PP_ERROR_ORGANIZATION_DUP, (error: any) => {
      if (!validate.name) {
        validate.name = {
          touched: true,
          error: false,
        };
      }
      validate.name.message = "";
      const newValidate: any = { ...validate };
      newValidate["name"]["error"] = true;
      newValidate["name"]["message"] = t("application_name_dup_error");
      setValidate(newValidate);
      setOpenTooltips({ name: newValidate.name.error });
    });
  }, [t, validate]);

  /** override for Tooltip.onClose */
  const handleCloseTooltip = (event: any) => {
    const elName = event.target.name;
    setOpenTooltips({ [elName]: false });
  };

  /** override for Tooltip.onOpen */
  const handleOpenTooltip = (event: any) => {
    const elName = event.target.name;
    setOpenTooltips({ [elName]: true });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const el: HTMLInputElement = event.target as HTMLInputElement;
  
    if (!el.name) {
      console.error("The input element has no name attribute.");
      return;
    }
   
    const updatedModel = JSON.parse(JSON.stringify(props.model[0]));
    updatedModel[el.name] = el.value;
   
    const name = el.name.trim().toLowerCase();
   
    if (name === "name") {
      const nameRegex = /[^0-9a-z]/gi;
      updatedModel[el.name] = el.value.replace(nameRegex, "");
    }
   

    /* if (name === "displayname") {
      const displayNameRegex = /[\^\\/:,;*?"<>|[\]{}!@#$%^`~&'.()_+=]/g;
      updatedModel[el.name] = el.value.replace(displayNameRegex, "");
    } */
   
    updateParentModel(updatedModel, el.name);
  };

  const sanitizeModel = (inputModel: any) => {
    const sanitized = JSON.parse(JSON.stringify(inputModel));
    Object.keys(sanitized).forEach((key) => {
      if (!(sanitized[key] instanceof Array)) {
        sanitized[key] = sanitized[key]?.trim().replace(/\s\s+/g, " ");
      }
    });
    return sanitized;
  };

  const handleFocusAndBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (event.type === "focus") {
      setOpenTooltips({ [event.target.name]: false }); // hide async errors
    }

    const el: HTMLInputElement = event.target as HTMLInputElement;
    const cleanModel = sanitizeModel(props.model[0]);
    updateParentModel(cleanModel, el.name);
  };

  const handleKeyPress = (event: any) => {
    const elName: string = event.target.name;
    const elValue: string = String.fromCharCode(event.which);

    if (!elName) {
      console.error("The input element has no name attribute.");
      return;
    }

    switch (elName.trim().toLowerCase()) {
      case "name":
        const nameRegex = /[^\W_]/g;
        if (!nameRegex.test(elValue)) {
          event.preventDefault();
          return;
        }
        break;
      default:
        break;
    }
  };

  const handlePaste = (event: any) => {
    const el = event.target;
    const elName = el.name;

    if (!el.name) {
      console.error("The input element has no name attribute.");
      return;
    }

    const updatedModel = JSON.parse(JSON.stringify(props.model[0]));
    switch (elName.trim().toLowerCase()) {
      case "name":
        setTimeout(() => {
          const nameRegex = /\W/g;
          updatedModel[elName] = el.value.replace(nameRegex, "");
          updateParentModel(updatedModel, elName);
        }, 20);
        break;
      default:
        break;
    }
  };

  const updateParentModel = (updatedModel: INameSubscriptionStepModel, fieldName: string) => {
    const isValid = validateField(updatedModel, fieldName);
    updatedModel.name = updatedModel.name?.toLowerCase();

    // Check if any field is different from previous values
    let isSame = true;
    if (previousAppName) {
      if (
        updatedModel.name !== previousAppName
      ) {
        isSame = false;
      }
    }

    props.handleModelChange({
      ...props.model,
      0: updatedModel,
      isValid: isValid,
      isSameValue: isSame
    });
  };

  const validateField = (
    updatedModel: INameSubscriptionStepModel,
    fieldName: string,
  ) => {
    const _validate = validateStep(
      updatedModel,
      validate
    );
    setValidate(_validate);

    let isValid: boolean = true;
    let errorField: string | undefined = Object.keys(_validate).find(
      (key: string) => _validate[key].error === true,
    );

    if (errorField) {
      isValid = false;
    }
    return isValid;
  };

  const handleCopyToClipboard = useCallback(async (ref: React.MutableRefObject<HTMLInputElement | null>) => {
    if (ref.current) {
      const textToCopy = ref.current.value;
      try {
        await navigator.clipboard.writeText(textToCopy);
        setCopyButtonText(t("copied") || "");
        setTimeout(() => {
          setCopyButtonText(t("click_to_copy") || "");
        }, 2000);
      } catch (err) {
        console.error("Failed to copy: ", err);
      }
    }
  }, [t]);

  const handleRegenerateId = async () => {
    if (!selectedApp || !appService.regenerateKey) {
      console.error("Selected app or appService is not available.");
      return;
    }
    
    try {
      const newKeyResponse = await appService.regenerateKey(config.tenantId, selectedApp);
        if(newKeyResponse)
        {
          const org = await appService.getApp(config.tenantId, selectedApp);
          if(org){
            const updatedModel = JSON.parse(JSON.stringify(props.model[0]));
            updatedModel.accessKey = org.subscriptionKey;
            updatedModel.clientId = org.clientID; //update other values as needed
            updateParentModel(updatedModel, "accessKey");
          }
        } else {
          console.error("Error getting new key", newKeyResponse);
        }
    } catch (error) {
      console.error("Error regenerating key:", error);
    }
  };

  return (
    <Box className={props.containerClass} sx={{ width: "701px" }}>
    <FormWrapper>
      <Typography variant="h5" sx={{ marginBottom: "8px", fontWeight: "700" }}>{t("application_information")}</Typography>
      <Box display="flex" flexDirection="column" alignItems="stretch">
        <StyledTooltip
          open={!!openTooltips.name && validate.name}
          onClose={handleCloseTooltip}
          onOpen={handleOpenTooltip}
          invalid={validate.name?.error}
          title={
            (validate.name &&
              t(validate.name.message, {
                item: t("application_name"),
                minLength: validate?.name?.minLength,
              })) ||
            ""
          }
          disableHoverListener
        >
          <TextField
            fullWidth
            error={
              validate.name && validate.name.touched && validate.name.error
            }
            id="txtApplicationName"
            variant="outlined"
            label={t("application_name")}
            name="name"
            value={props.model[0].name}
            inputProps={{
              maxLength: 36,
              style: { textTransform: "lowercase" },
            }}
            onChange={handleChange}
            onFocus={handleFocusAndBlur}
            onBlur={handleFocusAndBlur}
            onPaste={handlePaste}
            onKeyUp={handleKeyPress}
          />
        </StyledTooltip>
        {selectedApp && (
        <>
          <StyledTooltip
            open={!!openTooltips.clientId && validate.clientId}
            onClose={handleCloseTooltip}
            onOpen={handleOpenTooltip}
            invalid={validate.clientId && validate.clientId.error}
            title={
              (validate.clientId &&
              t(validate.clientId.message, {
                  item: t("client_id"),
              })) ||
                ""
            }
            disableHoverListener
          >
            <Box sx={{ display: "flex" }}>
              <TextField
                    id="txtApplicationClientId"
                    inputRef={clientIdRef}
                    name="clientId"
                    variant="outlined"
                    label={t("client_id")}
                    value={props.model[0].clientId}
                    onChange={handleChange}
                    onFocus={handleFocusAndBlur}
                    onBlur={handleFocusAndBlur}
                    onClick={handleKeyPress}
                    onPaste={handlePaste}
                    style={{width: "300px"}}
                    disabled
                    sx={{
                      "& .MuiInputLabel-root": { color: "black" }, // Label color
                      "& .MuiInputBase-root": { backgroundColor: "#f5f5f5" }, // Background color
                      "& .Mui-disabled": {
                        color: "black", // Text color
                        "-webkit-text-fill-color": "black", // Override default gray text
                      },
                    }}
              />
              <StyledButton
                    style={{ marginTop: "22px", whiteSpace: "nowrap", height: "36px"}}
                    variant="contained"
                    color="primary"
                    onClick={() => handleCopyToClipboard(clientIdRef)}
                    id="click-to-copy-button-client"
                  >
                    {t("click_to_copy")}
              </StyledButton>
            </Box>
          </StyledTooltip>
        
          <StyledTooltip
                open={!!openTooltips.accessKey && validate.accessKey}
                onClose={handleCloseTooltip}
                onOpen={handleOpenTooltip}
                invalid={validate.accessKey && validate.accessKey.error}
                title={
                  (validate.accessKey &&
                    t(validate.accessKey.message, {
                      item: t("api_access_key"),
                    })) ||
                  ""
                }
                disableHoverListener
          >
            <Box sx={{ display: "flex" }}>
              <TextField
                  id="txtApplicationAccessKey"
                  inputRef={accessKeyRef}
                  name="accessKey"
                  variant="outlined"
                  label={t("api_access_key")}
                  value={props.model[0].accessKey}
                  onChange={handleChange}
                  onFocus={handleFocusAndBlur}
                  onBlur={handleFocusAndBlur}
                  onClick={handleKeyPress}
                  onPaste={handlePaste}
                  style={{width: "300px"}}
                  disabled
                  sx={{
                    "& .MuiInputLabel-root": { color: "black" }, // Label color
                    "& .MuiInputBase-root": { backgroundColor: "#f5f5f5" }, // Background color
                    "& .Mui-disabled": {
                      color: "black", // Text color
                      "-webkit-text-fill-color": "black", // Override default gray text
                    },
                  }}
              />
                <>
                  <StyledButton
                    style={{ marginTop: "22px", whiteSpace: "nowrap", height: "36px"}}
                    variant="contained"
                    color="primary"
                    onClick={() => handleCopyToClipboard(accessKeyRef)}
                    id="click-to-copy-button-access"
                  >
                    {t("click_to_copy")}
                  </StyledButton>
                  <StyledButton
                    data-testid="regenerate-id-button"
                    style={{ marginTop: "22px", whiteSpace: "nowrap", height: "36px"}}
                    variant="contained"
                    color="primary"
                    onClick={handleRegenerateId}
                    id="regenerate-id-button"
                  >
                    {t("regenerate_id")}
                  </StyledButton>
                </>
            </Box>
          </StyledTooltip> 
        </>
        )}
      </Box> 
    </FormWrapper>
    </Box> 
  );
});

export default ApplicationUpsertForm;

